import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import { useIntl } from 'react-intl'
import { MAX_COMPARISON_SELECTIONS } from 'constants/offer'
import { stopPropagation } from 'lib/web/eventHandlerUtils'
import { rem } from 'polished'
import { useCallback } from 'react'
import styled from 'styled-components'
import Tooltip from 'LuxKit/Tooltip'
import { useAppSelector } from 'hooks/reduxHooks'
import { isSpoofed } from 'selectors/isSpoofed'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'

const Root = styled.div`
  display: inline-block;
  padding-bottom: ${rem(12)};
`

interface Props {
  cartItemId: string
  cartId: string
  offerType: App.OfferType
  offerId: string
  name: string
  roomId?: string
  packageUniqueKey?: string
  filters?: App.OfferListFilters
  priceTotals?: {
    price: number
    memberPrice: number
    value: number
  }
}

function MultiCartAccommodationTileComparisonCheckbox({
  packageUniqueKey,
  offerType,
  offerId,
  name,
  filters,
  roomId,
  priceTotals,
  cartItemId,
  cartId,
}: Props) {
  const intl = useIntl()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const currentSelections = useSelector(multiCartActorRef, (snapshot) => (snapshot.context.cartSpoofedSelections.status === 'loaded'
    ? snapshot.context.cartSpoofedSelections.data[cartId] || []
    : []
  ))

  const isSpoofedEnabled = useAppSelector(isSpoofed)
  const canAddSelection = currentSelections.length < MAX_COMPARISON_SELECTIONS
  const currentSelection = currentSelections.find(s => s.selectionId === cartItemId)
  const isSelected = !!currentSelection

  const onToggle = useCallback(() => {
    if (currentSelection) {
      multiCartActorRef.send({
        type: 'remove cart offer comparison selection',
        cartId,
        selectionId: currentSelection.selectionId,
      })
    } else {
      multiCartActorRef.send({
        type: 'add cart offer comparison selection',
        cartId,
        selection: {
          selectionId: cartItemId,
          offerType,
          offerId,
          name,
          priceTotals,
          params: { packageUniqueKey, roomId, filters },
        },
      })
    }
  }, [currentSelection, multiCartActorRef, cartId, cartItemId, offerType, offerId, name, roomId, filters, priceTotals, packageUniqueKey])

  const checkboxDisabled = !isSelected && !canAddSelection

  if (!isSpoofedEnabled) {
    return null
  }

  return (
    <Tooltip
      description={intl.formatMessage(
        {
          id: 'Ae0Dfbel4TD7niQVMdJY',
          defaultMessage: 'Only {maxSelections} offers can be compared at a time',
          description: 'Tooltip shown when maximum number of comparison selections has been reached',
        },
        { maxSelections: MAX_COMPARISON_SELECTIONS },
      )}
      disabled={!checkboxDisabled}
    >
      <Root onClick={stopPropagation}>
        <CheckboxInput
          onChange={onToggle}
          disabled={checkboxDisabled}
          checked={isSelected}
        >
          {intl.formatMessage({
            id: 'atO_sEV9GyQ17Q9BQQNH',
            defaultMessage: 'Compare',
            description: 'Checkbox label to select an accommodation item for comparison',
          })}
        </CheckboxInput>
      </Root>
    </Tooltip>
  )
}

export default MultiCartAccommodationTileComparisonCheckbox
